import { round } from '@/utils';
import { format } from 'date-fns';
import { atom } from 'jotai';
import { darken, lighten } from '@mui/material';

export const stateAtom = atom({
  query: {},
  parameters: {},
  time: null,
});

export function removeVehiclePrefixFromQuery(query) {
  return Object.entries(query).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key.replace(/^vehicle\./, '')]: value,
    }),
    {},
  );
}

export const pipelineFn = (query, time) => [
  { $match: removeVehiclePrefixFromQuery(query ?? {}) },
  {
    $set: {
      odometerReading: {
        $first: {
          $sortArray: {
            input: {
              $filter: {
                input: '$odometerReadings',
                cond: {
                  $lte: ['$$this.time', time ?? new Date()],
                },
              },
            },
            sortBy: {
              time: -1,
              kilometres: -1,
            },
          },
        },
      },
    },
  },
  {
    $lookup: {
      from: 'trips',
      localField: 'identificationNumber',
      foreignField: 'vehicle.identificationNumber',
      let: {
        time: {
          $ifNull: ['$odometerReading.time', new Date('1900-01-01')],
        },
      },
      pipeline: [
        {
          $match: {
            $expr: {
              $and: [
                {
                  $gte: ['$startTime', '$$time'],
                },
                {
                  $lte: ['$startTime', time ?? new Date()],
                },
              ],
            },
          },
        },
        {
          $group: {
            _id: null,
            startTime: {
              $min: '$startTime',
            },
            endTime: {
              $max: '$endTime',
            },
            durationSeconds: {
              $sum: '$durationSeconds',
            },
            count: {
              $sum: 1,
            },
            totalMiles: {
              $sum: {
                $round: [
                  {
                    $multiply: [
                      {
                        $ifNull: ['$distanceKilometres', 0],
                      },
                      0.62137119,
                    ],
                  },
                  2,
                ],
              },
            },
          },
        },
      ],
      as: 'trips',
    },
  },
  {
    $lookup: {
      from: 'stops',
      localField: 'identificationNumber',
      foreignField: 'vehicle.identificationNumber',
      let: {
        time: {
          $ifNull: ['$odometerReading.time', new Date('1900-01-01')],
        },
      },
      pipeline: [
        {
          $match: {
            $expr: {
              $and: [
                {
                  $gte: ['$startTime', '$$time'],
                },
                {
                  $lte: ['$startTime', time ?? new Date()],
                },
              ],
            },
          },
        },
        {
          $group: {
            _id: null,
            startTime: {
              $min: '$startTime',
            },
            endTime: {
              $max: '$endTime',
            },
            durationSeconds: {
              $sum: '$durationSeconds',
            },
            count: {
              $sum: 1,
            },
          },
        },
      ],
      as: 'stops',
    },
  },
  {
    $lookup: {
      from: 'stops',
      localField: 'identificationNumber',
      foreignField: 'vehicle.identificationNumber',
      let: {
        time: {
          $ifNull: ['$odometerReading.time', new Date('1900-01-01')],
        },
      },
      pipeline: [
        {
          $match: {
            $expr: {
              $and: [
                {
                  $lt: ['$startTime', '$$time'],
                },
                {
                  $gt: ['$endTime', '$$time'],
                },
              ],
            },
          },
        },
        {
          $project: {
            activityStartTime: {
              $max: ['$startTime', '$$time'],
            },
            startTime: true,
            endTime: true,
            durationSeconds: true,
          },
        },
        {
          $set: {
            activityDurationSeconds: {
              $dateDiff: {
                startDate: '$activityStartTime',
                endDate: '$endTime',
                unit: 'second',
              },
            },
          },
        },
      ],
      as: 'overlappingStop',
    },
  },
  {
    $set: {
      trips: {
        $arrayElemAt: ['$trips', 0],
      },
      stops: {
        $arrayElemAt: ['$stops', 0],
      },
      overlappingStop: {
        $arrayElemAt: ['$overlappingStop', 0],
      },
    },
  },
  {
    $project: {
      _id: false,
      registrationNumber: true,
      fleetNumber: true,
      role: true,
      type: true,
      trips: true,
      stops: true,
      overlappingStop: true,
      firstActivity: {
        $min: [
          '$trips.startTime',
          '$stops.startTime',
          '$overlappingStop.activityStartTime',
        ],
      },
      latestActivity: {
        $max: ['$trips.endTime', '$stops.endTime'],
      },
      odometerReading: {
        $cond: {
          if: {
            $eq: ['$odometerReading', null],
          },
          then: '$$REMOVE',
          else: {
            time: '$odometerReading.time',
            miles: {
              $round: [
                {
                  $multiply: ['$odometerReading.kilometres', 0.62137119],
                },
                2,
              ],
            },
          },
        },
      },
    },
  },
  {
    $set: {
      calculatedMileage: {
        $round: [
          {
            $add: [
              { $ifNull: ['$trips.totalMiles', 0] },
              { $ifNull: ['$odometerReading.miles', 0] },
            ],
          },
          2,
        ],
      },
      firstActivityFromOdometerDays: {
        $dateDiff: {
          startDate: '$odometerReading.time',
          endDate: '$firstActivity',
          unit: 'day',
        },
      },
      reportDurationSeconds: {
        $dateDiff: {
          startDate: '$odometerReading.time',
          endDate: '$latestActivity',
          unit: 'second',
        },
      },
      activityDurationSeconds: {
        $add: [
          '$stops.durationSeconds',
          '$trips.durationSeconds',
          {
            $ifNull: ['$overlappingStop.activityDurationSeconds', 0],
          },
        ],
      },
    },
  },
  {
    $set: {
      activityPercentage: {
        $cond: [
          {
            $gt: ['$reportDurationSeconds', 0],
          },
          {
            $round: [
              {
                $multiply: [
                  {
                    $divide: [
                      '$activityDurationSeconds',
                      '$reportDurationSeconds',
                    ],
                  },
                  100,
                ],
              },
              2,
            ],
          },
          null,
        ],
      },
    },
  },
  {
    $set: {
      error: {
        $switch: {
          branches: [
            {
              case: {
                $or: [
                  {
                    $eq: [
                      {
                        $type: '$odometerReading',
                      },
                      'missing',
                    ],
                  },
                  {
                    $in: ['$odometerReading.time', [null, '']],
                  },
                  {
                    $in: ['$odometerReading.miles', [null, '']],
                  },
                ],
              },
              then: 'NO_ODOMETER_READING',
            },
            {
              case: {
                $eq: ['$activityDurationSeconds', null],
              },
              then: 'NO_ACTIVITY_RECORDED',
            },
            // {
            //   case: {
            //     $gt: ['$firstActivityFromOdometerDays', 7],
            //   },
            //   then: 'GAP_ODOMETER_ACTIVITY',
            // },
            {
              case: {
                $and: [
                  {
                    $lt: ['$activityPercentage', 90],
                  },
                ],
              },
              then: 'LOW_COVERAGE',
            },
          ],
          default: null,
        },
      },
    },
  },
];

function getBackgroundColor(color, mode) {
  if (!color) return null;

  return mode === 'dark' ? darken(color, 0.9) : lighten(color, 0.9);
}

function getCellTitle(error) {
  switch (error) {
    case 'NO_ODOMETER_READING':
      return 'Vehicle has no odometer reading';
    case 'NO_ACTIVITY_RECORDED':
      return 'Vehicle has no activity after the odometer reading';
    case 'LOW_COVERAGE':
      return 'Vehicles activity covers less than 90% of the reporting period';
    default:
      return '';
  }
}

function getCellStyle({ cell, row, group, align, errorAlign }) {
  const error = row.original.error;
  const value = cell.getValue();

  let cellAlign = align;
  if (!value) {
    cellAlign = errorAlign ? errorAlign : 'center';
  }

  return {
    title: getCellTitle(error),
    align: cellAlign,
    sx: (theme) => {
      if (!error) return {};

      const palette = theme.palette;

      if (group === 'odometer' && !value) {
        return {
          backgroundColor: getBackgroundColor(palette.error.main, palette.mode),
          color: palette.error.main,
        };
      }
      if (group === 'activity') {
        if (!value) {
          return {
            backgroundColor: getBackgroundColor(
              palette.warning.main,
              palette.mode,
            ),
            color: palette.warning.main,
          };
        }
        if (error === 'LOW_COVERAGE') {
          return {
            backgroundColor: getBackgroundColor(
              palette.error.main,
              palette.mode,
            ),
            color: palette.error.main,
          };
        }
      }

      if (group === 'calculation') {
        if (error === 'NO_ODOMETER_READING') {
          return {
            backgroundColor: getBackgroundColor(
              palette.error.main,
              palette.mode,
            ),
            color: palette.error.main,
          };
        }

        if (error === 'NO_ACTIVITY_RECORDED') {
          return {
            backgroundColor: getBackgroundColor(
              palette.warning.main,
              palette.mode,
            ),
            color: palette.warning.main,
          };
        }

        if (error === 'LOW_COVERAGE') {
          return {
            backgroundColor: getBackgroundColor(
              palette.error.main,
              palette.mode,
            ),
            color: palette.error.main,
          };
        }
      }

      return {};
    },
  };
}

export const columns = [
  {
    header: 'Vehicle',
    id: 'vehicle',
    columns: [
      {
        header: 'Registration',
        id: 'registrationNumber',
        accessorFn: ({ registrationNumber }) => registrationNumber ?? '',
      },
      {
        header: 'Fleet Number',
        id: 'fleetNumber',
        accessorFn: ({ fleetNumber }) => fleetNumber ?? '',
      },
      {
        header: 'Role',
        id: 'role',
        accessorFn: ({ role }) => role ?? '',
      },
      {
        header: 'Type',
        id: 'type',
        accessorFn: ({ type }) => type ?? '',
      },
    ],
  },
  {
    header: 'Odometer',
    id: 'odometer',
    columns: [
      {
        header: 'Time',
        id: 'odometerReading.time',
        accessorFn: ({ odometerReading: { time } = {} }) => time ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'odometer',
          }),
        Cell: ({ cell }) =>
          cell.getValue()
            ? format(cell.getValue(), 'dd/MM/yyyy HH:mm:ss')
            : '-',
      },
      {
        header: 'Mileage',
        id: 'odometerReading.miles',
        accessorFn: ({ odometerReading: { miles } = {} }) => miles ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'odometer',
            align: 'right',
          }),
        Cell: ({ cell }) =>
          cell.getValue() ? `${round(cell.getValue(), 0)} mi` : '-',
        muiTableHeadCellProps: { align: 'right' },
      },
    ],
  },
  {
    header: 'GPS Activity',
    id: 'gpsActivity',
    columns: [
      {
        header: 'Start',
        id: 'firstActivity',
        accessorFn: ({ firstActivity }) => firstActivity ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'activity',
          }),
        Cell: ({ cell }) =>
          cell.getValue()
            ? format(cell.getValue(), 'dd/MM/yyyy HH:mm:ss')
            : '-',
      },
      {
        header: 'End',
        id: 'latestActivity',
        accessorFn: ({ latestActivity }) => latestActivity ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'activity',
          }),
        Cell: ({ cell }) =>
          cell.getValue()
            ? format(cell.getValue(), 'dd/MM/yyyy HH:mm:ss')
            : '-',
      },
      {
        header: 'Mileage',
        id: 'trips.totalMiles',
        accessorFn: ({ trips: { totalMiles } = {} }) => totalMiles ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'activity',
            align: 'right',
          }),
        Cell: ({ cell }) =>
          cell.getValue() ? `${round(cell.getValue(), 0)} mi` : '-',
        muiTableHeadCellProps: { align: 'right' },
      },
      {
        header: 'Coverage',
        id: 'activityPercentage',
        accessorFn: ({ activityPercentage }) => activityPercentage ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'activity',
            align: 'right',
            errorAlign: 'right',
          }),
        Cell: ({ cell }) =>
          cell.getValue() ? `${round(cell.getValue(), 0)} %` : '0 %',
        muiTableHeadCellProps: { align: 'right' },
      },
    ],
  },
  {
    header: 'Calculation',
    id: 'calculation',
    columns: [
      {
        header: 'Mileage',
        id: 'calculatedMileage',
        accessorFn: ({ calculatedMileage }) => calculatedMileage ?? '',
        muiTableBodyCellProps: ({ cell, row }) =>
          getCellStyle({
            cell,
            row,
            group: 'calculation',
            align: 'right',
            errorAlign: 'right',
          }),
        Cell: ({ cell }) =>
          cell.getValue() ? `${round(cell.getValue(), 0)} mi` : '0 mi',
        muiTableHeadCellProps: { align: 'right' },
      },
    ],
  },
];
